import { useState } from 'react';

export default function CookiesContent() {
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    analytics: false,
    personalization: false,
    advertising: false
  });

  const handleSavePreferences = () => {
    // Sauvegarder les préférences dans le localStorage
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    alert('Vos préférences ont été enregistrées avec succès !');
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      personalization: true,
      advertising: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    alert('Tous les cookies ont été acceptés !');
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      personalization: false,
      advertising: false
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    alert('Seuls les cookies nécessaires ont été conservés.');
  };

  return (
    <div className="pt-24 lg:pt-32 pb-12 lg:pb-20 px-4 lg:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8 lg:mb-16 text-center">
          <h1 className="text-2xl lg:text-5xl font-bold text-slate-900 mb-3 lg:mb-6">
            Politique de Cookies
          </h1>
          <p className="text-slate-600 text-sm lg:text-lg">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        {/* Introduction */}
        <div className="mb-6 lg:mb-12 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl lg:rounded-2xl p-4 lg:p-8 border border-emerald-100">
          <div className="flex items-start gap-3 lg:gap-4">
            <div className="w-10 h-10 lg:w-14 lg:h-14 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white flex-shrink-0">
              <i className="ri-file-text-line text-xl lg:text-3xl"></i>
            </div>
            <div>
              <h2 className="text-base lg:text-xl font-bold text-slate-900 mb-2 lg:mb-3">Qu'est-ce qu'un cookie ?</h2>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, smartphone) lors de la visite d'un site web. Il permet au site de mémoriser des informations sur votre visite, comme votre langue préférée et d'autres paramètres, afin de faciliter votre prochaine visite et de rendre le site plus utile.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6 lg:space-y-12">
          {/* Section 1 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-list-check text-sm lg:text-lg"></i>
              </span>
              Types de cookies utilisés
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-4 lg:space-y-6">
              <p className="text-slate-700 leading-relaxed mb-4 lg:mb-6 text-sm lg:text-base">
                Notre site utilise différents types de cookies, chacun ayant une finalité spécifique :
              </p>

              {/* Cookies nécessaires */}
              <div className="bg-white rounded-xl p-4 lg:p-6 border-2 border-emerald-200">
                <div className="flex flex-col lg:flex-row items-start justify-between mb-3 lg:mb-4 gap-3">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                      <i className="ri-shield-check-line text-lg lg:text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base lg:text-lg font-bold text-slate-900">Cookies strictement nécessaires</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Obligatoires - Ne peuvent pas être désactivés</p>
                    </div>
                  </div>
                  <div className="bg-emerald-100 text-emerald-700 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full text-xs lg:text-sm font-bold whitespace-nowrap">
                    Toujours actifs
                  </div>
                </div>
                <p className="text-slate-700 mb-3 lg:mb-4 text-sm lg:text-base">
                  Ces cookies sont indispensables au fonctionnement du site. Ils vous permettent de naviguer sur le site et d'utiliser ses fonctionnalités essentielles. Sans ces cookies, certains services ne peuvent pas être fournis.
                </p>
                <div className="bg-slate-50 rounded-lg p-3 lg:p-4">
                  <p className="text-xs lg:text-sm font-bold text-slate-900 mb-2">Exemples :</p>
                  <ul className="list-disc list-inside space-y-1 text-xs lg:text-sm text-slate-700 ml-2">
                    <li>Cookies de session pour maintenir votre connexion</li>
                    <li>Cookies de sécurité pour protéger contre les attaques</li>
                    <li>Cookies de préférence de langue</li>
                    <li>Cookies de panier d'achat (si applicable)</li>
                    <li>Cookies d'équilibrage de charge</li>
                  </ul>
                </div>
                <p className="text-xs text-slate-600 mt-3 lg:mt-4">
                  <strong>Durée de conservation :</strong> Session ou jusqu'à 12 mois maximum
                </p>
              </div>

              {/* Cookies analytiques */}
              <div className="bg-white rounded-xl p-4 lg:p-6 border-2 border-slate-200">
                <div className="flex flex-col lg:flex-row items-start justify-between mb-3 lg:mb-4 gap-3">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                      <i className="ri-bar-chart-line text-lg lg:text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base lg:text-lg font-bold text-slate-900">Cookies analytiques et de performance</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Nécessitent votre consentement</p>
                    </div>
                  </div>
                </div>
                <p className="text-slate-700 mb-3 lg:mb-4 text-sm lg:text-base">
                  Ces cookies nous permettent de mesurer l'audience du site, d'analyser son utilisation et d'améliorer ses performances. Ils collectent des informations anonymes sur la façon dont les visiteurs utilisent le site.
                </p>
                <div className="bg-slate-50 rounded-lg p-3 lg:p-4">
                  <p className="text-xs lg:text-sm font-bold text-slate-900 mb-2">Exemples :</p>
                  <ul className="list-disc list-inside space-y-1 text-xs lg:text-sm text-slate-700 ml-2">
                    <li>Google Analytics : analyse du trafic et du comportement des visiteurs</li>
                    <li>Statistiques de pages visitées et durée de visite</li>
                    <li>Taux de rebond et parcours utilisateur</li>
                    <li>Sources de trafic (référencement, réseaux sociaux, etc.)</li>
                    <li>Performances techniques du site</li>
                  </ul>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 lg:p-4 mt-3 lg:mt-4">
                  <p className="text-xs lg:text-sm text-slate-700">
                    <i className="ri-information-line text-blue-500 mr-2"></i>
                    <strong>Données collectées :</strong> Adresse IP anonymisée, type de navigateur, système d'exploitation, pages visitées, durée de visite, source de référencement
                  </p>
                </div>
                <p className="text-xs text-slate-600 mt-3 lg:mt-4">
                  <strong>Durée de conservation :</strong> 25 mois maximum (conformément aux recommandations CNIL)
                </p>
              </div>

              {/* Cookies de personnalisation */}
              <div className="bg-white rounded-xl p-4 lg:p-6 border-2 border-slate-200">
                <div className="flex flex-col lg:flex-row items-start justify-between mb-3 lg:mb-4 gap-3">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                      <i className="ri-user-smile-line text-lg lg:text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base lg:text-lg font-bold text-slate-900">Cookies de personnalisation</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Nécessitent votre consentement</p>
                    </div>
                  </div>
                </div>
                <p className="text-slate-700 mb-3 lg:mb-4 text-sm lg:text-base">
                  Ces cookies permettent au site de mémoriser vos choix et préférences pour vous offrir une expérience personnalisée lors de vos prochaines visites.
                </p>
                <div className="bg-slate-50 rounded-lg p-3 lg:p-4">
                  <p className="text-xs lg:text-sm font-bold text-slate-900 mb-2">Exemples :</p>
                  <ul className="list-disc list-inside space-y-1 text-xs lg:text-sm text-slate-700 ml-2">
                    <li>Préférences d'affichage (mode sombre/clair)</li>
                    <li>Taille de police préférée</li>
                    <li>Région ou ville sélectionnée</li>
                    <li>Mémorisation de vos choix de filtres</li>
                    <li>Contenu recommandé basé sur vos intérêts</li>
                  </ul>
                </div>
                <p className="text-xs text-slate-600 mt-3 lg:mt-4">
                  <strong>Durée de conservation :</strong> 12 mois maximum
                </p>
              </div>

              {/* Cookies publicitaires */}
              <div className="bg-white rounded-xl p-4 lg:p-6 border-2 border-slate-200">
                <div className="flex flex-col lg:flex-row items-start justify-between mb-3 lg:mb-4 gap-3">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                      <i className="ri-advertisement-line text-lg lg:text-xl"></i>
                    </div>
                    <div>
                      <h3 className="text-base lg:text-lg font-bold text-slate-900">Cookies publicitaires et de ciblage</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Nécessitent votre consentement</p>
                    </div>
                  </div>
                </div>
                <p className="text-slate-700 mb-3 lg:mb-4 text-sm lg:text-base">
                  Ces cookies peuvent être déposés par nos partenaires publicitaires via notre site. Ils permettent de créer un profil de vos intérêts et de vous proposer des publicités pertinentes sur d'autres sites.
                </p>
                <div className="bg-slate-50 rounded-lg p-3 lg:p-4">
                  <p className="text-xs lg:text-sm font-bold text-slate-900 mb-2">Exemples :</p>
                  <ul className="list-disc list-inside space-y-1 text-xs lg:text-sm text-slate-700 ml-2">
                    <li>Google Ads : publicités ciblées</li>
                    <li>Facebook Pixel : retargeting publicitaire</li>
                    <li>LinkedIn Insight Tag : publicités B2B</li>
                    <li>Cookies de réseaux publicitaires tiers</li>
                  </ul>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 lg:p-4 mt-3 lg:mt-4">
                  <p className="text-xs lg:text-sm text-slate-700">
                    <i className="ri-alert-line text-amber-500 mr-2"></i>
                    <strong>Important :</strong> Ces cookies ne collectent pas directement vos données personnelles identifiables, mais créent un profil basé sur votre navigation.
                  </p>
                </div>
                <p className="text-xs text-slate-600 mt-3 lg:mt-4">
                  <strong>Durée de conservation :</strong> 13 mois maximum
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 - Gestionnaire de préférences */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-settings-3-line text-sm lg:text-lg"></i>
              </span>
              Gérer vos préférences cookies
            </h2>
            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl p-4 lg:p-8 border-2 border-slate-200">
              <p className="text-slate-700 mb-6 lg:mb-8 text-sm lg:text-base">
                Vous pouvez à tout moment modifier vos préférences concernant les cookies. Activez ou désactivez les catégories de cookies selon vos souhaits :
              </p>

              <div className="space-y-3 lg:space-y-4">
                {/* Cookie nécessaire */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-1 text-sm lg:text-base">Cookies strictement nécessaires</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Requis pour le fonctionnement du site</p>
                    </div>
                    <div className="bg-emerald-100 text-emerald-700 px-3 lg:px-4 py-1.5 lg:py-2 rounded-full text-xs lg:text-sm font-bold whitespace-nowrap">
                      Toujours actifs
                    </div>
                  </div>
                </div>

                {/* Cookies analytiques */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-1 text-sm lg:text-base">Cookies analytiques</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Nous aident à améliorer le site</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.analytics}
                        onChange={(e) => setCookiePreferences({ ...cookiePreferences, analytics: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 lg:w-14 lg:h-8 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-5 lg:peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 lg:after:top-1 after:left-0.5 lg:after:left-1 after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 lg:after:h-6 lg:after:w-6 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </div>

                {/* Cookies de personnalisation */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-1 text-sm lg:text-base">Cookies de personnalisation</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Mémorisent vos préférences</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.personalization}
                        onChange={(e) => setCookiePreferences({ ...cookiePreferences, personalization: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 lg:w-14 lg:h-8 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-5 lg:peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 lg:after:top-1 after:left-0.5 lg:after:left-1 after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 lg:after:h-6 lg:after:w-6 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </div>

                {/* Cookies publicitaires */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 mb-1 text-sm lg:text-base">Cookies publicitaires</h3>
                      <p className="text-xs lg:text-sm text-slate-600">Publicités personnalisées</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={cookiePreferences.advertising}
                        onChange={(e) => setCookiePreferences({ ...cookiePreferences, advertising: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 lg:w-14 lg:h-8 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-5 lg:peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 lg:after:top-1 after:left-0.5 lg:after:left-1 after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 lg:after:h-6 lg:after:w-6 after:transition-all peer-checked:bg-emerald-500"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="mt-6 lg:mt-8 flex flex-col sm:flex-row gap-3 lg:gap-4">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 lg:px-6 py-3 lg:py-4 rounded-xl font-bold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm lg:text-base"
                >
                  <i className="ri-save-line text-lg lg:text-xl"></i>
                  Enregistrer mes préférences
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 bg-white text-emerald-600 border-2 border-emerald-500 px-4 lg:px-6 py-3 lg:py-4 rounded-xl font-bold hover:bg-emerald-50 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm lg:text-base"
                >
                  <i className="ri-checkbox-circle-line text-lg lg:text-xl"></i>
                  Tout accepter
                </button>
                <button
                  onClick={handleRejectAll}
                  className="flex-1 bg-white text-slate-600 border-2 border-slate-300 px-4 lg:px-6 py-3 lg:py-4 rounded-xl font-bold hover:bg-slate-50 transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 text-sm lg:text-base"
                >
                  <i className="ri-close-circle-line text-lg lg:text-xl"></i>
                  Tout refuser
                </button>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-computer-line text-sm lg:text-lg"></i>
              </span>
              Gérer les cookies via votre navigateur
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-4 lg:space-y-6">
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Vous pouvez également gérer les cookies directement depuis les paramètres de votre navigateur. Voici comment procéder selon votre navigateur :
              </p>

              <div className="grid md:grid-cols-2 gap-3 lg:gap-4">
                {/* Chrome */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-slate-100">
                      <i className="ri-chrome-line text-lg lg:text-xl text-slate-600"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">Google Chrome</h3>
                  </div>
                  <ol className="list-decimal list-inside space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-700">
                    <li>Cliquez sur le menu (⋮) &gt; Paramètres</li>
                    <li>Confidentialité et sécurité</li>
                    <li>Cookies et autres données de sites</li>
                    <li>Gérez vos préférences</li>
                  </ol>
                  <a 
                    href="https://support.google.com/chrome/answer/95647" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-xs lg:text-sm font-semibold mt-3 lg:mt-4 cursor-pointer"
                  >
                    En savoir plus <i className="ri-external-link-line"></i>
                  </a>
                </div>

                {/* Firefox */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-slate-100">
                      <i className="ri-firefox-line text-lg lg:text-xl text-slate-600"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">Mozilla Firefox</h3>
                  </div>
                  <ol className="list-decimal list-inside space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-700">
                    <li>Cliquez sur le menu (☰) &gt; Paramètres</li>
                    <li>Vie privée et sécurité</li>
                    <li>Cookies et données de sites</li>
                    <li>Gérez les permissions</li>
                  </ol>
                  <a 
                    href="https://support.mozilla.org/fr/kb/cookies-informations-sites-enregistrent" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-xs lg:text-sm font-semibold mt-3 lg:mt-4 cursor-pointer"
                  >
                    En savoir plus <i className="ri-external-link-line"></i>
                  </a>
                </div>

                {/* Safari */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-slate-100">
                      <i className="ri-safari-line text-lg lg:text-xl text-slate-600"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">Safari</h3>
                  </div>
                  <ol className="list-decimal list-inside space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-700">
                    <li>Safari &gt; Préférences</li>
                    <li>Onglet Confidentialité</li>
                    <li>Gérer les données de sites web</li>
                    <li>Supprimez ou bloquez les cookies</li>
                  </ol>
                  <a 
                    href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-xs lg:text-sm font-semibold mt-3 lg:mt-4 cursor-pointer"
                  >
                    En savoir plus <i className="ri-external-link-line"></i>
                  </a>
                </div>

                {/* Edge */}
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <div className="flex items-center gap-2 lg:gap-3 mb-3 lg:mb-4">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-slate-100">
                      <i className="ri-edge-line text-lg lg:text-xl text-slate-600"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">Microsoft Edge</h3>
                  </div>
                  <ol className="list-decimal list-inside space-y-1.5 lg:space-y-2 text-xs lg:text-sm text-slate-700">
                    <li>Cliquez sur le menu (⋯) &gt; Paramètres</li>
                    <li>Confidentialité, recherche et services</li>
                    <li>Cookies et données de site</li>
                    <li>Gérez et supprimez les cookies</li>
                  </ol>
                  <a 
                    href="https://support.microsoft.com/fr-fr/microsoft-edge/supprimer-les-cookies-dans-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 text-xs lg:text-sm font-semibold mt-3 lg:mt-4 cursor-pointer"
                  >
                    En savoir plus <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg p-4 lg:p-6 border border-amber-200 mt-4 lg:mt-6">
                <p className="text-slate-700 text-xs lg:text-sm">
                  <i className="ri-alert-line text-amber-500 mr-2"></i>
                  <strong>Attention :</strong> La désactivation de tous les cookies peut affecter le fonctionnement de certains sites web et limiter votre expérience de navigation.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-list-unordered text-sm lg:text-lg"></i>
              </span>
              Liste détaillée des cookies
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-4 lg:space-y-6">
              <p className="text-slate-700 leading-relaxed mb-4 lg:mb-6 text-sm lg:text-base">
                Voici la liste exhaustive des cookies utilisés sur notre site :
              </p>

              <div className="overflow-x-auto -mx-4 lg:mx-0">
                <div className="inline-block min-w-full align-middle px-4 lg:px-0">
                  <table className="w-full bg-white rounded-lg overflow-hidden">
                    <thead className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                      <tr>
                        <th className="px-3 lg:px-6 py-3 lg:py-4 text-left font-semibold text-xs lg:text-base">Nom du cookie</th>
                        <th className="px-3 lg:px-6 py-3 lg:py-4 text-left font-semibold text-xs lg:text-base">Catégorie</th>
                        <th className="px-3 lg:px-6 py-3 lg:py-4 text-left font-semibold text-xs lg:text-base">Finalité</th>
                        <th className="px-3 lg:px-6 py-3 lg:py-4 text-left font-semibold text-xs lg:text-base">Durée</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      <tr>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 font-mono text-xs lg:text-sm">PHPSESSID</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4"><span className="bg-emerald-100 text-emerald-700 px-2 lg:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">Nécessaire</span></td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Maintien de la session utilisateur</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Session</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 font-mono text-xs lg:text-sm">cookieConsent</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4"><span className="bg-emerald-100 text-emerald-700 px-2 lg:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">Nécessaire</span></td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Mémorisation du consentement cookies</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">12 mois</td>
                      </tr>
                      <tr>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 font-mono text-xs lg:text-sm">_ga</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4"><span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">Analytique</span></td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Google Analytics - Identification visiteur</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">25 mois</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 font-mono text-xs lg:text-sm">_gid</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4"><span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">Analytique</span></td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Google Analytics - Distinction visiteurs</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">24 heures</td>
                      </tr>
                      <tr>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 font-mono text-xs lg:text-sm">_gat</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4"><span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">Analytique</span></td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Google Analytics - Limitation requêtes</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">1 minute</td>
                      </tr>
                      <tr className="bg-slate-50">
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 font-mono text-xs lg:text-sm">userPrefs</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4"><span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">Personnalisation</span></td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">Préférences d'affichage utilisateur</td>
                        <td className="px-3 lg:px-6 py-3 lg:py-4 text-slate-700 text-xs lg:text-sm">12 mois</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <p className="text-slate-700 text-xs lg:text-sm mt-4 lg:mt-6">
                Cette liste est mise à jour régulièrement. Pour toute question concernant un cookie spécifique, n'hésitez pas à nous contacter.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-links-line text-sm lg:text-lg"></i>
              </span>
              Cookies tiers et partenaires
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-4 lg:space-y-6">
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Certains cookies sont déposés par des services tiers pour améliorer votre expérience ou analyser l'utilisation du site :
              </p>

              <div className="space-y-3 lg:space-y-4">
                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-2 lg:mb-3 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-google-line text-slate-600"></i>
                    Google Analytics
                  </h3>
                  <p className="text-slate-700 text-xs lg:text-sm mb-2 lg:mb-3">
                    Service d'analyse d'audience qui nous aide à comprendre comment les visiteurs utilisent notre site.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2 lg:mb-3">
                    <span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs">_ga</span>
                    <span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs">_gid</span>
                    <span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs">_gat</span>
                  </div>
                  <a 
                    href="https://policies.google.com/privacy" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 text-xs lg:text-sm font-semibold cursor-pointer"
                  >
                    Politique de confidentialité Google <i className="ri-external-link-line"></i>
                  </a>
                </div>

                <div className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200">
                  <h3 className="font-bold text-slate-900 mb-2 lg:mb-3 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-facebook-box-line text-slate-600"></i>
                    Facebook Pixel (si applicable)
                  </h3>
                  <p className="text-slate-700 text-xs lg:text-sm mb-2 lg:mb-3">
                    Outil de suivi publicitaire permettant de mesurer l'efficacité de nos campagnes Facebook.
                  </p>
                  <div className="flex flex-wrap gap-2 mb-2 lg:mb-3">
                    <span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs">_fbp</span>
                    <span className="bg-slate-100 text-slate-700 px-2 lg:px-3 py-1 rounded-full text-xs">fr</span>
                  </div>
                  <a 
                    href="https://www.facebook.com/privacy/explanation" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-emerald-600 hover:text-emerald-700 text-xs lg:text-sm font-semibold cursor-pointer"
                  >
                    Politique de confidentialité Facebook <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              <div className="bg-blue-50 rounded-lg p-4 lg:p-6 border border-blue-200 mt-4 lg:mt-6">
                <p className="text-slate-700 text-xs lg:text-sm">
                  <i className="ri-information-line text-blue-500 mr-2"></i>
                  <strong>Note :</strong> Ces services tiers ont leurs propres politiques de confidentialité. Nous vous encourageons à les consulter pour comprendre comment ils traitent vos données.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-shield-user-line text-sm lg:text-lg"></i>
              </span>
              Vos droits et protection des données
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-4 lg:space-y-6">
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Conformément au RGPD, vous disposez de droits sur les données collectées via les cookies :
              </p>

              <div className="grid md:grid-cols-2 gap-3 lg:gap-4">
                <div className="bg-white rounded-lg p-4 lg:p-6 border-l-4 border-emerald-500">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-eye-line text-emerald-500"></i>
                    Droit d'accès
                  </h3>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Vous pouvez demander quelles données sont collectées via les cookies.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 lg:p-6 border-l-4 border-emerald-500">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-delete-bin-line text-emerald-500"></i>
                    Droit d'effacement
                  </h3>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Vous pouvez supprimer les cookies à tout moment depuis votre navigateur.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 lg:p-6 border-l-4 border-emerald-500">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-close-circle-line text-emerald-500"></i>
                    Droit d'opposition
                  </h3>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Vous pouvez refuser les cookies non nécessaires via notre gestionnaire.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-4 lg:p-6 border-l-4 border-emerald-500">
                  <h3 className="font-bold text-slate-900 mb-2 flex items-center gap-2 text-sm lg:text-base">
                    <i className="ri-user-unfollow-line text-emerald-500"></i>
                    Retrait du consentement
                  </h3>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Vous pouvez retirer votre consentement à tout moment.
                  </p>
                </div>
              </div>

              <div className="bg-emerald-50 rounded-lg p-4 lg:p-6 border border-emerald-200 mt-4 lg:mt-6">
                <h3 className="font-bold text-slate-900 mb-2 lg:mb-3 flex items-center gap-2 text-sm lg:text-base">
                  <i className="ri-question-line text-emerald-600"></i>
                  Comment exercer vos droits ?
                </h3>
                <p className="text-slate-700 mb-3 lg:mb-4 text-xs lg:text-sm">
                  Pour toute question ou demande concernant les cookies et vos données :
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-center gap-2 text-xs lg:text-sm">
                    <i className="ri-mail-line text-emerald-500"></i>
                    Email : <strong>contact@powereno.fr</strong>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-refresh-line text-sm lg:text-lg"></i>
              </span>
              Modifications de la politique cookies
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-3 lg:space-y-4">
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                Nous nous réservons le droit de modifier cette politique de cookies à tout moment pour refléter les changements dans nos pratiques ou pour nous conformer aux évolutions légales et réglementaires.
              </p>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                En cas de modification substantielle, nous vous en informerons via un bandeau sur le site ou par email si vous êtes client.
              </p>
              <p className="text-slate-700 leading-relaxed text-sm lg:text-base">
                La date de dernière mise à jour est indiquée en haut de cette page. Nous vous encourageons à consulter régulièrement cette politique.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-lg lg:text-2xl font-bold text-slate-900 mb-4 lg:mb-6 flex items-center gap-2 lg:gap-3">
              <span className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                <i className="ri-links-fill text-sm lg:text-lg"></i>
              </span>
              Ressources utiles
            </h2>
            <div className="bg-slate-50 rounded-xl p-4 lg:p-8 space-y-3 lg:space-y-4">
              <p className="text-slate-700 leading-relaxed mb-4 lg:mb-6 text-sm lg:text-base">
                Pour en savoir plus sur les cookies et la protection de vos données :
              </p>

              <div className="grid md:grid-cols-2 gap-3 lg:gap-4">
                <a 
                  href="https://www.cnil.fr/fr/cookies-et-autres-traceurs" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200 hover:border-emerald-500 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <i className="ri-government-line text-lg lg:text-xl"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">CNIL</h3>
                  </div>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Guide officiel sur les cookies et traceurs
                  </p>
                  <div className="flex items-center gap-2 text-emerald-600 text-xs lg:text-sm font-semibold mt-2 lg:mt-3">
                    Consulter <i className="ri-external-link-line"></i>
                  </div>
                </a>

                <a 
                  href="https://www.youronlinechoices.com/fr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200 hover:border-emerald-500 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <i className="ri-settings-3-line text-lg lg:text-xl"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">Your Online Choices</h3>
                  </div>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Plateforme de gestion des cookies publicitaires
                  </p>
                  <div className="flex items-center gap-2 text-emerald-600 text-xs lg:text-sm font-semibold mt-2 lg:mt-3">
                    Consulter <i className="ri-external-link-line"></i>
                  </div>
                </a>

                <a 
                  href="https://www.cnil.fr/fr/modeles/rgpd" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200 hover:border-emerald-500 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <i className="ri-shield-check-line text-lg lg:text-xl"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">RGPD</h3>
                  </div>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Comprendre le Règlement Général sur la Protection des Données
                  </p>
                  <div className="flex items-center gap-2 text-emerald-600 text-xs lg:text-sm font-semibold mt-2 lg:mt-3">
                    Consulter <i className="ri-external-link-line"></i>
                  </div>
                </a>

                <a 
                  href="https://www.allaboutcookies.org/fr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-lg p-4 lg:p-6 border border-slate-200 hover:border-emerald-500 transition-colors cursor-pointer group"
                >
                  <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-3">
                    <div className="w-8 h-8 lg:w-10 lg:h-10 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                      <i className="ri-information-line text-lg lg:text-xl"></i>
                    </div>
                    <h3 className="font-bold text-slate-900 text-sm lg:text-base">All About Cookies</h3>
                  </div>
                  <p className="text-slate-700 text-xs lg:text-sm">
                    Tout savoir sur les cookies et comment les gérer
                  </p>
                  <div className="flex items-center gap-2 text-emerald-600 text-xs lg:text-sm font-semibold mt-2 lg:mt-3">
                    Consulter <i className="ri-external-link-line"></i>
                  </div>
                </a>
              </div>
            </div>
          </section>
        </div>

        {/* Contact CTA */}
        <div className="mt-8 lg:mt-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl lg:rounded-2xl p-6 lg:p-10 text-center text-white">
          <div className="w-12 h-12 lg:w-16 lg:h-16 flex items-center justify-center rounded-full bg-white/20 mx-auto mb-4 lg:mb-6">
            <i className="ri-question-answer-line text-2xl lg:text-3xl"></i>
          </div>
          <h3 className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4">Des questions sur notre utilisation des cookies ?</h3>
          <p className="text-white/90 mb-6 lg:mb-8 max-w-2xl mx-auto text-sm lg:text-base">
            Notre équipe est à votre disposition pour répondre à toutes vos questions concernant les cookies et la protection de vos données.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 justify-center">
            <a 
              href="mailto:contact@powereno.fr"
              className="inline-flex items-center justify-center gap-2 bg-white text-emerald-600 px-6 lg:px-8 py-3 lg:py-4 rounded-full font-semibold hover:scale-105 transition-transform cursor-pointer whitespace-nowrap text-sm lg:text-base"
            >
              <i className="ri-mail-send-line text-lg lg:text-xl"></i>
              Nous contacter par email
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
